import { queryClient } from '@/queryClient';

/**
 * Invalidate the MuvalTable list queries for an endpoint.
 *
 * MuvalTable keys its query on the full `url` prop, which usually carries query
 * params (e.g. `/transports?method=ROAD&include_containers=true`), so a store
 * can't reconstruct the exact key — match on the path prefix instead.
 *
 * Needed because the global query `staleTime` is 30s (src/queryClient.ts): a
 * form that saves and redirects straight back to its index lands inside that
 * window, so the table serves the pre-edit rows from cache until a hard
 * refresh. Call this from entity stores after a mutation, alongside
 * `invalidateResourceFilterCache`.
 */
export function invalidateTableQueries(path: string): void {
    queryClient.invalidateQueries({
        predicate: (query) => typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith(path),
    });
}

/**
 * Cache-key prefixes for every list surface that renders a row's move fields —
 * the category chip, space, dates. `/orders/list` is the Leads index table,
 * `/orders/jobs` the Jobs and Affiliate-leads tables, `/global-search/orders`
 * the search dropdown, and `kanban` covers both the Leads kanban board query
 * (`kanban-board`) and the per-column tray queries (`kanban`).
 */
const ORDER_LIST_QUERY_PREFIXES = ['/orders/list', '/orders/jobs', '/global-search/orders', 'kanban'];

/**
 * Invalidate the cached order lists after a move mutation.
 *
 * Editing a move from the order page (e.g. the move category changer) leaves
 * those lists holding pre-edit rows: they're inactive queries while the order
 * page is open, so this only marks them stale and they refetch on next mount
 * rather than firing a request now.
 */
export function invalidateOrderListQueries(): void {
    ORDER_LIST_QUERY_PREFIXES.forEach(invalidateTableQueries);
}
